"use client";

import { useCallback } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useGlobalModalStack, type ModalId } from "@west-tennessee-consulting/ui";
import {
  buildCleanUrl,
  getModalConfig,
  isModalUrlEnabled,
  parseHubModalUrlState,
  type ConfiguredModalId,
} from "../url/modal-url-config";
import { buildModalUrl } from "../url/modal-url-builder";
import {
  beginNavigation,
  useModalHistoryStore,
} from "../history/modal-history-store";

type UrlModalParams = Record<string, string | undefined>;
type ModalData = Record<string, unknown>;

export interface HubModalOpenOptions {
  /**
   * Overwrite the current history entry instead of pushing a new one.
   *
   * Defaults to `false`: opening a URL-configured modal creates a history
   * entry so the browser Back button closes it (and Forward re-opens it).
   * Pass `true` for modals that must not be re-openable by Forward — e.g. a
   * confirmation shown as the result of an action.
   *
   * Re-opening a modal that is ALREADY open (switching which entity it shows —
   * prev/next, or clicking another row while the modal is up) always replaces,
   * whatever this says: the modal owns exactly one history entry for as long as
   * it is open, so browsing twenty deals inside it leaves twenty stale entries
   * behind it instead of one.
   */
  replace?: boolean;
  preserveParams?: boolean;
  additionalParams?: Record<string, string>;
  data?: ModalData;
}

export interface HubModalCloseOptions {
  /**
   * Force a URL rewrite instead of unwinding the pushed history entry.
   * Defaults to `false` — a modal that pushed a history entry closes by
   * going Back, so closing never grows the history stack.
   */
  replace?: boolean;
}

export interface HubModalController {
  openModal: (
    modalId: ModalId,
    params?: UrlModalParams,
    options?: HubModalOpenOptions
  ) => void;
  closeModal: (modalId: ModalId, options?: HubModalCloseOptions) => void;
  closeAll: (options?: HubModalCloseOptions) => void;
  isOpen: (modalId: ModalId) => boolean;
  getModalUrl: (modalId: ConfiguredModalId, params: UrlModalParams) => string;
}

/** Split a URL we built ourselves back into the parts the builders want. */
function splitUrl(url: string): {
  pathname: string;
  searchParams: URLSearchParams;
} {
  const queryStart = url.indexOf("?");
  if (queryStart === -1) {
    return { pathname: url, searchParams: new URLSearchParams() };
  }
  return {
    pathname: url.slice(0, queryStart),
    searchParams: new URLSearchParams(url.slice(queryStart + 1)),
  };
}

/** The entity a set of open-params points at, in either param spelling. */
function entityIdFromParams(
  modalId: ConfiguredModalId,
  params: UrlModalParams
): string | null {
  const config = getModalConfig(modalId);
  return params[config.pluralParam] ?? params[config.singularParam] ?? null;
}

/**
 * Hub modal controller: stack-authoritative modal behavior, with single-modal
 * URL deep-linking AND browser-history participation.
 *
 * ## The stack is the truth; the URL is a projection of it
 *
 * `router.push`/`router.back()` commit on a later tick and `useSearchParams`
 * reports the result a render after that, so ANY decision made by reading the
 * URL is made against a stale snapshot. Opening therefore never consults the
 * URL: it opens the stack synchronously and unconditionally, then writes the
 * URL to match. `store.pendingUrl` records the projection we asked the router
 * for, which lets `useModalHistorySync` tell a URL that has caught up from one
 * belonging to a superseded navigation.
 *
 * That ordering is what makes fast interaction safe. Closing a modal and
 * immediately clicking another row used to open the new modal and then have
 * the close's still-in-flight `router.back()` land and reconcile it straight
 * back shut. Now the new modal is already open in the stack before the stale
 * popstate arrives, and the sync effect ignores that popstate because the URL
 * it carries is not the one we are navigating to.
 *
 * History contract (see `lib/modals/history/`): opening a URL-configured modal
 * pushes one entry, switching entities inside it rewrites that entry, closing
 * it unwinds the entry (`router.back()`), and `useModalHistorySync` reconciles
 * the stack whenever the user drives history themselves. Modals with no URL
 * config are stack-only and never touch history — they have no URL
 * representation, so a Forward that "restored" them would restore an empty
 * modal.
 */
export function useHubModalController(): HubModalController {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const history = useModalHistoryStore();
  const {
    openModal: openGlobalModal,
    closeModal: closeGlobalModal,
    closeAllModals,
    isModalOpen,
  } = useGlobalModalStack();

  const observedUrl = useCallback(() => {
    const safePathname = pathname ?? "";
    const query = searchParams?.toString();
    return `${safePathname}${query ? `?${query}` : ""}`;
  }, [pathname, searchParams]);

  /**
   * The URL we are on, or heading to. During an in-flight navigation the
   * observed URL is the one we are leaving, so every URL decision has to be
   * made against the pending target instead.
   */
  const effectiveUrl = useCallback(
    () => history.pendingUrl ?? observedUrl(),
    [history, observedUrl]
  );

  /**
   * Ask the router for a URL, and remember it until it is observed. Returns
   * whether a navigation actually happened: asking for the URL we are already
   * on is a no-op, and the caller must not then record a history entry it does
   * not own (closing it would `back()` past something it never pushed).
   */
  const navigate = useCallback(
    (url: string, mode: "push" | "replace"): boolean => {
      if (url === effectiveUrl()) return false;
      beginNavigation(history, url, observedUrl());
      if (mode === "replace") {
        router.replace(url);
      } else {
        router.push(url);
      }
      return true;
    },
    [history, router, effectiveUrl, observedUrl]
  );

  const openModal = useCallback(
    (modalId: ModalId, params: UrlModalParams = {}, options: HubModalOpenOptions = {}) => {
      const { replace = false, preserveParams = true, additionalParams = {}, data = {} } = options;
      const isUrlModal = isModalUrlEnabled(modalId);
      const mergedModalData = {
        ...params,
        ...additionalParams,
        ...data,
      };

      if (!isUrlModal) {
        openGlobalModal(modalId, mergedModalData);
        return;
      }

      const targetEntityId = entityIdFromParams(modalId, params);
      const existing = history.entries.find((entry) => entry.modalId === modalId);

      // Idempotent: this modal is already open on this entity. A second click
      // (or a double-click) must not push a second history entry for the same
      // thing — the close would then need two Backs to unwind and the sync
      // effect would re-open the modal on the first.
      if (existing && existing.entityId === targetEntityId && isModalOpen(modalId)) {
        return;
      }

      // Open FIRST, synchronously, before touching the router. The modal is up
      // on the same tick as the click no matter what navigation is in flight.
      openGlobalModal(modalId, mergedModalData);

      // Rewrite the current history entry rather than stacking a new one when
      // we already own one: the modal is open and only its entity is changing
      // (prev/next, or another row while it is up).
      const shouldReplace = replace || Boolean(existing);
      const from = splitUrl(effectiveUrl());
      const url = buildModalUrl(modalId, params, {
        pathname: from.pathname,
        searchParams: from.searchParams,
        preserveParams,
        additionalParams,
      });
      const urlBefore = effectiveUrl();
      const didNavigate = navigate(url, shouldReplace ? "replace" : "push");
      const didPush = didNavigate && !shouldReplace;

      history.entries = existing
        ? history.entries.map((entry) =>
            entry.modalId === modalId ? { ...entry, entityId: targetEntityId } : entry
          )
        : [
            ...history.entries,
            {
              modalId,
              entityId: targetEntityId,
              pushed: didPush,
              urlBefore: didPush ? urlBefore : null,
              observedOpen: false,
            },
          ];
    },
    [history, effectiveUrl, navigate, openGlobalModal, isModalOpen]
  );

  const closeModal = useCallback(
    (modalId: ModalId, options: HubModalCloseOptions = {}) => {
      const isUrlModal = isModalUrlEnabled(modalId);
      closeGlobalModal(modalId);
      if (!isUrlModal) return;

      const index = history.entries.findIndex((entry) => entry.modalId === modalId);
      const entry = index === -1 ? null : history.entries[index];
      if (index !== -1) {
        history.entries = history.entries.slice(0, index);
      }

      // Only unwind when this modal still owns the URL we are on or heading to
      // — if something else navigated in the meantime, `router.back()` would
      // rewind the wrong entry. Rewriting the URL in place is always safe.
      const target = splitUrl(effectiveUrl());
      const stillCurrent =
        parseHubModalUrlState(target.searchParams)?.modalId === modalId;

      if (entry?.pushed && entry.urlBefore !== null && !options.replace && stillCurrent) {
        // `back()` is the one navigation whose destination we cannot compute,
        // so it was recorded when we pushed.
        beginNavigation(history, entry.urlBefore, observedUrl());
        router.back();
        return;
      }

      navigate(buildCleanUrl(target.pathname, target.searchParams, modalId), "replace");
    },
    [history, effectiveUrl, observedUrl, navigate, router, closeGlobalModal]
  );

  const closeAll = useCallback(
    (options: HubModalCloseOptions = {}) => {
      const target = splitUrl(effectiveUrl());
      const parsed = parseHubModalUrlState(target.searchParams);
      closeAllModals();

      const pushed = history.entries.filter((entry) => entry.pushed);
      const outermostPushed = pushed[0];
      history.entries = [];

      if (!parsed) return;

      // Unwind every entry we pushed in one go so the pre-modal page is one
      // Back away, exactly as it was before the first modal opened. The App
      // Router exposes only `back()`/`forward()`, so a multi-step rewind goes
      // through the History API — Next picks the resulting popstate up.
      const unwindTo = outermostPushed?.urlBefore ?? null;
      if (
        pushed.length > 0 &&
        unwindTo !== null &&
        !options.replace &&
        typeof window !== "undefined"
      ) {
        beginNavigation(history, unwindTo, observedUrl());
        window.history.go(-pushed.length);
        return;
      }

      navigate(
        buildCleanUrl(target.pathname, target.searchParams, parsed.modalId),
        "replace"
      );
    },
    [history, effectiveUrl, observedUrl, navigate, closeAllModals]
  );

  const isOpen = useCallback(
    (modalId: ModalId) => isModalOpen(modalId),
    [isModalOpen]
  );

  const getModalUrl = useCallback(
    (modalId: ConfiguredModalId, params: UrlModalParams) =>
      buildModalUrl(modalId, params, {
        pathname: pathname ?? "",
        searchParams,
        preserveParams: true,
      }),
    [pathname, searchParams]
  );

  return {
    openModal,
    closeModal,
    closeAll,
    isOpen,
    getModalUrl,
  };
}
